<template>
    <div
        id="main-menu"
        class="main-menu"
        :class="{ 'main-menu--mounted': isMounted }"
    >
        <div class="py-4">
            <div class="container">
                <div class="flex items-center max-lg:justify-between">
                    <a
                        class="block mr-10"
                        href="/"
                    >
                        <logo class="block w-auto h-10" />
                    </a>

                    <button
                        type="button"
                        class="menu"
                        :class="{ 'menu--open': isMenuOpen }"
                        aria-expanded="true"
                        aria-controls="main-menu"
                        @click.prevent="onToggleMenu"
                    >
                        <span class="sr-only">Close menu</span>

                        <span class="menu__wrapper">
                            <span class="menu__line" />
                            <span class="menu__line" />
                            <span class="menu__line" />
                        </span>
                    </button>
                </div>
            </div>
        </div>

        <div class="relative flex-1">
            <div
                ref="scrolling"
                class="absolute inset-0 overflow-y-auto"
            >
                <div class="container">
                    <nav class="py-8">
                        <ul class="space-y-1">
                            <li
                                v-for="(node, index) in nodes"
                                :key="node.id"
                                class="menu__node"
                                :style="{
                                    transitionDelay: `${index * 80}ms`,
                                }"
                            >
                                <span class="flex relative w-full justify-between items-center rounded-xl px-4 py-3 -mx-4 hover:bg-white/[0.04] transition-colors duration-150">
                                    <a
                                        v-if="node.attributes.href"
                                        v-bind="node.attributes"
                                    >
                                        <span
                                            v-if="node.icon"
                                            v-html="node.icon"
                                        />

                                        <span>{{ node.text }}</span>
                                    </a>

                                    <button
                                        v-else-if="node.children.length > 0"
                                        v-bind="node.attributes"
                                        type="button"
                                        @click="onSetNode(node.id)"
                                    >{{ node.text }}</button>

                                    <span
                                        v-else
                                        v-bind="node.attributes"
                                    >{{ node.text }}</span>

                                    <button
                                        v-if="node.children.length > 0"
                                        class="absolute right-4 top-1/2 -translate-y-1/2 p-1"
                                        type="button"
                                        @click="onSetNode(node.id)"
                                    >
                                        <chevron-right
                                            class="block w-3 h-auto text-white/40 transition-transform duration-300 ease-in-out"
                                            :class="{ 'transform rotate-90 text-white': activeNode === node.id }"
                                        />
                                    </button>
                                </span>

                                <collapse-transition>
                                    <ul
                                        v-if="node.children.length > 0 && activeNode === node.id"
                                        class="py-2 space-y-0.5"
                                    >
                                        <li
                                            v-for="child in node.children"
                                            :key="child.id"
                                            class="menu__subnode"
                                        >
                                            <a
                                                v-bind="child.attributes"
                                                v-text="child.text"
                                            />
                                        </li>
                                    </ul>
                                </collapse-transition>
                            </li>
                        </ul>
                    </nav>

                    <!-- Contact section -->
                    <div class="menu__contact border-t border-white/[0.08] pt-8 pb-6">
                        <p class="font-heading text-[11px] font-semibold uppercase tracking-[2px] text-white/30 mb-4">Get in touch</p>

                        <div class="space-y-3">
                            <a
                                v-if="contactPhone"
                                :href="`tel:+61${contactPhone.replace(/^0|\D/g, '')}`"
                                class="flex items-center gap-3 text-white/60 hover:text-white transition-colors duration-150"
                            >
                                <span
                                    class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                                    style="background: rgba(1,74,173,0.1);"
                                >
                                    <svg
                                        class="w-3.5 h-3.5 text-brand-text"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                    ><path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" /></svg>
                                </span>
                                <span class="text-sm font-medium">{{ contactPhone }}</span>
                            </a>

                            <a
                                v-if="decodedEmail"
                                :href="`mailto:${decodedEmail}`"
                                class="flex items-center gap-3 text-white/60 hover:text-white transition-colors duration-150"
                            >
                                <span
                                    class="flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
                                    style="background: rgba(1,74,173,0.1);"
                                >
                                    <svg
                                        class="w-3.5 h-3.5 text-brand-text"
                                        fill="currentColor"
                                        viewBox="0 0 24 24"
                                    ><path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" /></svg>
                                </span>
                                <span class="text-sm font-medium">{{ decodedEmail }}</span>
                            </a>
                        </div>

                        <div
                            v-if="socials.length > 0"
                            class="flex gap-2 mt-6"
                        >
                            <a
                                v-for="social in socials"
                                :key="social.handle"
                                class="flex items-center justify-center w-10 h-10 rounded-xl text-white/40 hover:text-white transition-colors duration-150"
                                style="background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.06);"
                                :href="social.href"
                                rel="noopener noreferrer"
                                target="_blank"
                            >
                                <span class="sr-only">{{ social.handle }}</span>
                                <component
                                    :is="socialIcons[social.handle]"
                                    v-if="socialIcons[social.handle]"
                                    class="block w-5 h-5"
                                />
                            </a>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
    import {
        type Component,
        computed,
        nextTick,
        onBeforeUnmount,
        onMounted,
        ref,
        useTemplateRef,
    } from 'vue';

    import ChevronRight from '@/img/icons/chevron-right-regular.svg?component';
    import FacebookIcon from '@/img/icons/socials/facebook.svg?component';
    import InstagramIcon from '@/img/icons/socials/instagram.svg?component';
    import LinkedinIcon from '@/img/icons/socials/linkedin.svg?component';
    import TwitterIcon from '@/img/icons/socials/twitter.svg?component';
    import Logo from '@/img/logo-white.svg?component';

    interface AbstractNode {
        id: string;
        url: string;
        text: string;
        attributes: Record<string, any>;
    }

    interface Node extends AbstractNode {
        icon: string;
        attributes: Record<string, any>;
        children: Array<AbstractNode>;
    };

    const props = defineProps<{
        nodes: Node[];
        isMenuOpen: boolean;
        contactPhone: string;
        contactEmail: string;
        socials: {
            handle: 'facebook' | 'instagram' | 'linkedin' | 'twitter';
            href: string;
        }[];
    }>();

    const emits = defineEmits<{
        close: [];
    }>();

    const socialIcons = {
        facebook: FacebookIcon as Component,
        instagram: InstagramIcon as Component,
        linkedin: LinkedinIcon as Component,
        twitter: TwitterIcon as Component,
    };

    const scrolling = useTemplateRef<HTMLDivElement>('scrolling');
    const isMounted = ref<boolean>(false);
    const activeNode = ref<string | null>(null);

    const decodedEmail = computed<string>(() =>
        // @ts-expect-error
        props.contactEmail?.toString().replace(/[a-zA-Z]/g, c => String.fromCharCode((c <= 'Z' ? 90 : 122) >= (c = c.charCodeAt(0) + 13) ? c : c - 26)));

    onMounted(async() => {
        disableBodyScroll(scrolling.value!, {
            reserveScrollBarGap: true,
        });

        await nextTick();

        isMounted.value = true;
    });

    onBeforeUnmount(() => {
        enableBodyScroll(scrolling.value!);
    });

    const onToggleMenu = () => {
        emits('close');
    };

    const onSetNode = (nodeId: string) => {
        activeNode.value = activeNode.value === nodeId ? null : nodeId;
    };
</script>

<style>
    @reference '../../css/style.css';

    .main-menu {
        @apply flex flex-col fixed inset-0 text-white z-50;
        background: #0C0820;
    }

    /* Decorative glow */
    .main-menu::before {
        content: '';
        position: absolute;
        top: -100px;
        right: -60px;
        width: 300px;
        height: 300px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(1, 74, 173, 0.1) 0%, transparent 60%);
        pointer-events: none;
    }

    .menu__node {
        @apply block opacity-0;
        @apply transition-all duration-500 ease-in-out;
        transform: translateX(-12px);
    }

    .menu__link {
        @apply text-left text-white font-heading font-bold block flex-1;
        font-size: 1.25rem;
        letter-spacing: -0.3px;
        @apply transition-colors duration-300 ease-in-out;
    }

    .menu__subnode {
        @apply pl-5 ml-1;
        border-left: 2px solid rgba(255, 255, 255, 0.08);
    }

    .menu__subnode .menu__link {
        @apply py-2;
        font-size: 0.9375rem;
        font-weight: 500;
        color: rgba(255, 255, 255, 0.6);
    }

    .menu__subnode .menu__link:hover {
        color: #fff;
    }

    .main-menu--mounted .menu__node {
        @apply opacity-100;
        transform: translateX(0);
    }
</style>
